// src/encounter/structured-note/complaint-lines.tsx
//
// PROD-3042 · frame v2 ONLY (structured-note-section.tsx is the only caller, and it renders only with
// `?ef=frameV2`). Design desk D-72, as amended after the cross-model review (6 Oct 2026):
//
//  · The chief complaint's attributes are NOTE LINES: one line per attribute group the complaint's data
//    supplies, in the data's order, labelled with the data's own title ("Relieving factors" stays).
//  · Each group's options are VISIBLE CHIPS to click (19 Oct, Antonin: "let's bypass D-72", the prototype's table: a
//    label column and wrapping chips; the CLAUDE.md HPI register row is amended): the template's own options, labels
//    and order, one chip on for a single-choice group (`isSingleChoiceCategory`), several where the group allows several;
//    a long list shows its first CHIPS_SHOWN chips (and every chosen one) with "+N more". Replaces the closed PickListChip
//    (D-72, 6 Oct). Unset reads as unselected chips.
//  · Always visible: no "Show details" fold, and the DS chip matrix (ChiefComplaintHpiBlock's cards) is
//    never mounted once a complaint is recorded. The block still draws the empty, loading and error states,
//    where it holds no cards.
//  · EACH COMPLAINT IS ITS OWN SECTION (19 Oct, Antonin: each has its own sub-parameters): a heading with the complaint's
//    name, its lines below, a hairline and the section spacing between one complaint and the next.
//  · Each line keeps its Clear, each complaint keeps its remove, and "Add another complaint" stays.
//  · NO COMMENT AFFORDANCE (L7, coordinator 6 Oct): the per-attribute comment has no wire home — the page wires
//    no `onSaveComment` and CreateChiefComplaint carries no field for it — so a typed comment was lost. It is not
//    offered until a save path exists (a gap for Andrew).
//  · Sex: a group the CONTENT marks for the other sex (`templateItem.genderCategory`, `HpitemplateItems.
//    GenderCategory`: 'Male' / 'Female') is not drawn; no sex on record hides nothing. A group that already
//    holds a recorded value is always drawn, so a recorded answer is never hidden.
//  · Associated signs and symptoms (D-72 final + its 6 Oct copy amendment): HPI offers NO new associated
//    options — new findings are captured once, in the ROS. ONE read-only line, labelled with the data's own
//    group title: the answers recorded in HPI ("… · recorded in HPI"), then the present ROS findings for this
//    complaint's systems ("… · from ROS"), each source in text-secondary; and "Add in ROS", which moves focus to
//    the ROS block heading (hidden after the provider sign). Both empty → no line.
//  · No option is filtered here by sex, age or words. Sex and age conditions come from template content
//    (enableWhen / SDC answerOptionsToggleExpression, lib/patient-context.ts); these options come from the
//    legacy complaint document, which carries no option-level condition.
//  · THE WRITE DOES NOT CHANGE. Every change goes out through the block props the page already supplies
//    (`onToggle`, `onClearCategory`, `onRemove`, `onAdd`, `onSearch`) — the same CreateChiefComplaint
//    replace-all, the same `patientHpi` entries. Switch OFF never reaches this file.
//  · A provider sign freezes the complaint (an encounter record, rule 38): the host strips every write
//    handler, so the lines read as text ("Not asked" when empty), with no Clear, remove or picker.
//
// Built from parts the app already has (no DS change): NoteLine, Button and the add line.
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  ChiefComplaintHpiBlock,
  NoteLine,
  type AuthorRole,
  type ChiefComplaintHpiBlockProps,
  type ChiefComplaintHpiCategory,
  type ChiefComplaintHpiComplaint,
  type ChiefComplaintHpiOption,
} from '@scripsteam/scrips-signal-ds';
import { Check, Trash2 } from 'lucide-react';
import { categoryFitsSex, isSingleChoiceCategory } from '../lib/chief-complaint-hpi';
import { ApAddLine, type AddLineEntry, type AddLineStatus } from '../ap-add-line';
import {
  ADD_ANOTHER_COMPLAINT,
  ADD_CHIEF_COMPLAINT,
  ADD_IN_ROS,
  ASSOCIATED_FROM_HPI,
  ASSOCIATED_FROM_ROS,
  CHIEF_COMPLAINT_NOT_RECORDED,
  isAssociatedCategory,
} from './complaint-lines-model';

export { CHIPS_SHOWN } from './chip-choice';
import { CHIPS_SHOWN } from './chip-choice';

export interface ComplaintLinesProps {
  /** The block props the page resolved, with every write handler already stripped when frozen. */
  block: ChiefComplaintHpiBlockProps;
  /** The patient's registration sex ('M' / 'F'); anything else is no sex on record. */
  sex?: string | null;
  /** The present ROS findings (their labels) for this complaint's systems — the Associated line's ROS group. */
  rosFindingsFor?: (complaint: ChiefComplaintHpiComplaint) => readonly string[];
  /** Move focus to the ROS block heading ("Add in ROS"). Absent → no button (no ROS block in this note). */
  onAddInRos?: () => void;
  /** Frozen by a provider sign (rule 38): every line reads as text. */
  frozen: boolean;
  /** Who is writing (the add line's menu offers its rows to this author). */
  role?: AuthorRole;
}

/**
 * The chief complaint and its attribute lines. With no complaint yet (or while the document loads, or
 * when it failed), the DS block draws its picker and its own state — it holds no cards then.
 */
export function ComplaintLines({ block, frozen, sex, rosFindingsFor, onAddInRos, role = 'physician' }: ComplaintLinesProps) {
  const complaints = block.complaints ?? [];
  if (block.loading || block.error) {
    return (
      <>
        {/* `complaints: []`: the chip matrix is never mounted with the switch ON (D-72). No `onAdd`: the DS block's
            always-open picker is an empty text box in the note (ENC-L10); it only reports the load state here. */}
        <ChiefComplaintHpiBlock {...block} onAdd={undefined} complaints={[]} />
      </>
    );
  }
  if (complaints.length === 0) {
    // K8 (6 Oct contract): no complaint yet → the line says so, with ONE quiet "Add chief complaint" that opens the
    // same search-and-add field the DS block's picker runs. Never an empty box at rest (ENC-L10).
    return (
      <div className="flex flex-col gap-2" data-chief-complaint="empty">
        <NoteLine label="Chief complaint" align="center" valueClassName="flex flex-wrap items-center gap-2">
          {!frozen && block.onAdd ? (
            <ComplaintAddLine onAdd={block.onAdd} onSearch={block.onSearch} label={ADD_CHIEF_COMPLAINT} role={role} />
          ) : (
            <span className="text-text-secondary" data-testid="cc-not-recorded">
              {CHIEF_COMPLAINT_NOT_RECORDED}
            </span>
          )}
        </NoteLine>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2" data-chief-complaint data-complaints={complaints.length}>
      {/* K8 (6 Oct contract; integration, Codex P2): ONE add line at the top of the list it adds to. */}
      {!frozen && block.onAdd && (
        <NoteLine label="" align="center">
          <ComplaintAddLine onAdd={block.onAdd} onSearch={block.onSearch} label={ADD_ANOTHER_COMPLAINT} role={role} />
        </NoteLine>
      )}
      {complaints.map((complaint) => (
        <section
          key={complaint.id}
          className="flex flex-col gap-1"
          data-complaint={complaint.id}
          aria-label={`Chief complaint: ${complaint.heading ?? complaint.display}`}
        >
          <NoteLine
            align="start"
            label="Chief complaint"
            valueClassName="flex flex-wrap items-center gap-2"
          >
            <h4 className="m-0" data-complaint-heading>
              {complaint.heading ?? complaint.display}
            </h4>
            {!frozen && block.onRemove && (
              <button
                type="button"
                aria-label={`Remove ${complaint.display}`}
                className="icon-action"
                data-delete-control=""
                onClick={() => block.onRemove?.(complaint)}
              >
                <Trash2 className="size-4" aria-hidden />
              </button>
            )}
          </NoteLine>
          {complaint.categories.filter((category) => categoryFitsSex(category, sex) || category.options.some((o) => o.selected)).map((category) =>
            isAssociatedCategory(category) ? (
              <AssociatedLine
                key={category.id}
                label={category.title || 'Attribute'}
                category={category}
                fromRos={rosFindingsFor?.(complaint) ?? []}
                onAddInRos={frozen ? undefined : onAddInRos}
              />
            ) : (
              <AttributeLine key={category.id} block={block} complaint={complaint} category={category} frozen={frozen} />
            ),
          )}
        </section>
      ))}
      {block.saving && (
        <p role="status" aria-live="polite" className="m-0 font-sans text-size-sm text-text-secondary">
          Saving…
        </p>
      )}
    </div>
  );
}

/** One attribute group as a table row: label | chips (the template's own options) · Clear. */
function AttributeLine({
  block,
  complaint,
  category,
  frozen,
}: {
  block: ChiefComplaintHpiBlockProps;
  complaint: ChiefComplaintHpiComplaint;
  category: ChiefComplaintHpiCategory;
  frozen: boolean;
}) {
  const label = category.title || 'Attribute';
  const single = isSingleChoiceCategory(category);
  const selected = category.options.filter((o) => o.selected);
  const writable = !frozen && !!block.onToggle;
  const [expanded, setExpanded] = useState(false);
  if (!writable) {
    // Read only (a provider sign, or no write handler): the recorded values as text, "Not asked" when there are none.
    return (
      <div className="flex flex-col gap-1" data-attribute={category.id}>
        <NoteLine label={label} align="start" valueClassName="flex flex-wrap items-center gap-2">
          {selected.length > 0 ? (
            <span data-cc-value>{selected.map((o) => o.label).join(', ')}</span>
          ) : (
            <span className="text-text-secondary" data-cc-value>
              Not asked
            </span>
          )}
        </NoteLine>
      </div>
    );
  }
  const pick = (option: ChiefComplaintHpiOption) => {
    // Any click is the page's own toggle (which replaces the group's value for a single-choice group): the same
    // CreateChiefComplaint write as before. The last chosen chip of a group switched off is a Clear (ONE write, as the
    // pick list's last removal was).
    if (option.selected && (single || selected.length === 1) && block.onClearCategory) block.onClearCategory(complaint, category);
    else block.onToggle?.(complaint, category, option);
  };
  const shown = expanded ? category.options : category.options.filter((o, i) => i < CHIPS_SHOWN || o.selected);
  const hidden = category.options.length - shown.length;
  return (
    <div className="flex flex-col gap-1" data-attribute={category.id}>
      <NoteLine label={label} align="start" valueClassName="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={label} data-cc-chips className="flex flex-wrap items-center gap-2">
          {shown.map((option) => (
            <button
              key={option.id}
              type="button"
              data-cc-chip=""
              aria-pressed={!!option.selected}
              onClick={() => pick(option)}
            >
              {option.selected && <Check className="size-4" aria-hidden />}
              {option.label}
            </button>
          ))}
          {hidden > 0 && (
            <Button variant="tertiary" size="sm" aria-expanded={false} onClick={() => setExpanded(true)}>
              +{hidden} more
            </Button>
          )}
          {expanded && category.options.length > CHIPS_SHOWN && (
            <Button variant="tertiary" size="sm" aria-expanded onClick={() => setExpanded(false)}>
              Show fewer
            </Button>
          )}
          {selected.length > 0 && block.onClearCategory && (
            <Button variant="tertiary" size="sm" aria-label={`Clear ${label}`} onClick={() => block.onClearCategory?.(complaint, category)}>
              Clear
            </Button>
          )}
        </div>
      </NoteLine>
    </div>
  );
}

/** The Associated line: the HPI group, then the ROS group, each with its source; the "Add in ROS" action.
 *  Read-only (nothing here writes). Both groups empty → no line. */
function AssociatedLine({
  label,
  category,
  fromRos,
  onAddInRos,
}: {
  label: string;
  category: ChiefComplaintHpiCategory;
  fromRos: readonly string[];
  onAddInRos?: () => void;
}) {
  const fromHpi = category.options.filter((o) => o.selected).map((o) => o.label);
  if (fromHpi.length === 0 && fromRos.length === 0) return null;
  const group = (labels: readonly string[], source: string, part: string) => (
    <span data-part={part}>
      <span className="font-semibold">{labels.join(', ')}</span>
      <span className="text-text-secondary"> · {source}</span>
    </span>
  );
  return (
    <div data-attribute={category.id} data-associated>
      <NoteLine label={label} align="start" valueClassName="flex flex-col items-start gap-1">
        {fromHpi.length > 0 && group(fromHpi, ASSOCIATED_FROM_HPI, 'associated-hpi')}
        {fromRos.length > 0 && group(fromRos, ASSOCIATED_FROM_ROS, 'associated-ros')}
        {onAddInRos && (
          <Button variant="tertiary" size="sm" onClick={onAddInRos}>
            {ADD_IN_ROS}
          </Button>
        )}
      </NoteLine>
    </div>
  );
}

/** The complaint catalogue's rows shown at once (L2, K8: at most 8, the "as typed" row included). */
const COMPLAINT_ROWS = 8;
export const COMPLAINT_SOURCE = 'Complaint catalogue';
export const COMPLAINT_AS_TYPED = 'As typed';
export const COMPLAINTS_NO_MATCH = 'No complaints match';

/**
 * K8 (6 Oct contract; integration, Codex P2): the chief complaint's ONE add line — the same "Add …" line Diagnoses and
 * Orders use (ap-add-line.tsx `ApAddLine`: a quiet action at rest, the DS CommandMenu once typed, at most 8 rows). Rows
 * are the page's own catalogue search (`onSearch`, the latest text wins), each naming its source; the last row adds the
 * text as typed (the old picker's pencil). A pick goes out through the page's unchanged `onAdd`. Complaints are uncoded
 * clinic labels (authority §3), so a row carries its source, never an invented code.
 */
function ComplaintAddLine({
  onAdd,
  onSearch,
  label,
  role,
}: {
  onAdd: (text: string) => void;
  onSearch?: (query: string) => Promise<readonly string[]>;
  label: string;
  role: AuthorRole;
}) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ q: string; rows: string[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const search = (q: string) => {
    const id = ++requestId.current;
    setFailed(false);
    if (!onSearch || q.trim() === '') {
      setFound(null);
      return;
    }
    onSearch(q.trim()).then(
      (rows) => {
        if (mounted.current && id === requestId.current) setFound({ q: q.trim(), rows: [...rows] });
      },
      () => {
        if (mounted.current && id === requestId.current) setFailed(true);
      },
    );
  };
  const typed = query.trim();
  const current = found && found.q === typed ? found.rows : [];
  const exact = current.some((r) => r.trim().toLowerCase() === typed.toLowerCase());
  const catalogue = current.slice(0, typed && !exact ? COMPLAINT_ROWS - 1 : COMPLAINT_ROWS);
  const entries: AddLineEntry<string>[] = [
    ...catalogue.map((name) => ({ key: `cc:${name}`, kind: 'Complaint', term: name, hint: COMPLAINT_SOURCE, value: name })),
    ...(typed && !exact ? [{ key: 'cc:typed', kind: COMPLAINT_AS_TYPED, term: typed, hint: 'Not from the catalogue', value: typed }] : []),
  ];
  const answered = !!found && found.q === typed;
  const status: AddLineStatus = !typed ? 'idle' : failed ? 'error' : !onSearch || answered ? (current.length === 0 && entries.length === 0 ? 'empty' : 'idle') : 'searching';
  return (
    <ApAddLine
      label={label}
      query={query}
      onQuery={(next) => {
        setQuery(next);
        search(next);
      }}
      entries={entries}
      status={status}
      emptyText={COMPLAINTS_NO_MATCH}
      onRetry={() => search(query)}
      onPick={(value) => onAdd(value)}
      role={role}
    />
  );
}
